/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { isEqual, cloneDeep } from 'lodash';
import ShortNameField from '../Common/shortNameField';
import ResultDetailsStatus from './resultDetailsStatus';
import DetailsHeader from './resultDetailsHeader';
import ResultDetailsTab from './resultDetailsTab';
import ResultTasksTab from './resultTasksTab';
import ResultCadenceSelect from './resultCadenceSelect';
import Metrics from './metrics';
import ModalNavigation from '../Common/modalNavigation';
import TargetSelect from '../Common/targetSelect';
import ShareButton from '../Common/shareButton';
import modalTypes from '../../constants/modalTypes';
import { WorkPackageStatuses } from '../../constants/storyStatusTypes';
import { openModal, updateModal } from '../../actions/modalActions';
import { hasCompletedDevTask } from '../../utils/taskHelpers';
import {
  updateWorkPackage,
  deleteWorkPackage,
  addWorkPackageToCadence,
  removeWorkPackageFromCadence,
  createWorkPackage,
  restoreWorkPackage
} from '../../actions/workPackageActions';
import { allows, handleResultAuthorityChange } from '../../utils/authorization';
import { applicationError } from '../../actions/notificationActions';
import {
  isProductAssociatedWithSpace,
  isTeamAssociatedWithSpace
} from '../../utils/spaceHelpers';
import {
  handleOrgLevelChange,
  defaultNewResultAttributes,
  getModalProductTeam
} from '../../utils/resultModalHelpers';

class ResultDetail extends React.Component {
  constructor(props) {
    super(props);
    const prefilledValues = { ...defaultNewResultAttributes(props, 'tasks') };
    const result =
      props.result.id > 0 ? { ...props.result } : { ...prefilledValues.result };
    this.state = {
      ...prefilledValues,
      isShortNameValid: true,
      taskAttachments: {},
      isTaskDetailOpen: false,
      activeTab: 'details',
      result
    };
  }

  componentDidMount() {
    this.checkIfItemExists();
  }

  componentDidUpdate(prevProps) {
    const { result } = this.props;

    if (!isEqual(prevProps.result, result) && !!result.id) {
      const allowEdit =
        result.team_id || result.product_id ? allows('update', result) : true;
      const resultClone = cloneDeep(result);

      if (allowEdit && !!result.id) {
        delete resultClone.description;
      }

      this.setResultValue(resultClone);
    }

    this.checkIfItemExists();
  }

  setResultValue = (result) => {
    this.setState({ result, hasChanged: false });
  };

  setTaskDetailOpen = (isOpen) => {
    this.setState({ isTaskDetailOpen: isOpen });
  };

  checkIfItemExists = () => {
    if (this.props.id && !(this.props.result && this.props.result.id)) {
      this.props.onClose();

      this.props.applicationError(
        'Link you tried to visit is no longer valid.'
      );
    }
  };

  changeOrgLevel = (orgLevel) => {
    const { result } = this.props;
    this.setState({ changingOrgLevel: true }, () => {
      handleOrgLevelChange(orgLevel, result, this.closeForm);
      if (result.level === orgLevel.level) {
        this.setState({ changingOrgLevel: false });
      }
    });
  };

  showModal = (message, actions) => {
    this.props.openModal(
      modalTypes.message,
      {
        message,
        actions,
        closeButtonTitle: 'Cancel'
      },
      'confirm'
    );
  };

  handleSelectChange = (currValue, name) => {
    const { result } = this.state;
    if (this.state.result[name] === currValue && currValue !== null) return;
    const { updateWorkPackage, onClose } = this.props;
    if (!result.id) {
      let newResult;

      if (name === 'product_id' || name === 'team_id') {
        newResult = handleResultAuthorityChange(result, name, currValue);
      } else {
        newResult = { ...result, [name]: currValue };
      }

      this.setState({ result: newResult, hasChanged: true });
      return;
    }

    const field = { work_package: { [name]: currValue } };

    const message = `The ${
      name.split('_')[0]
    } you have selected is not part of the active space; this item will not be visible. Continue?`;

    const actions = {
      continue: {
        name: 'Continue',
        function: () => {
          updateWorkPackage(result.id, field);
          onClose();
        }
      }
    };

    if (name === 'product_id') {
      if (isProductAssociatedWithSpace(currValue))
        updateWorkPackage(result.id, field);
      else this.showModal(message, actions);
    } else if (name === 'team_id') {
      if (isTeamAssociatedWithSpace(currValue))
        updateWorkPackage(result.id, field);
      else this.showModal(message, actions);
    } else updateWorkPackage(result.id, field);
  };

  handleNewFieldChange = (e) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    let hasChanged = true;
    if (e.short_name) {
      resultClone.short_name = e.short_name;
      this.setState({ isShortNameValid: true });
    } else if (e.description) {
      resultClone.description = e.description;
    } else if (e.owner_id || e.owner_id === null) {
      resultClone.owner_id = e.owner_id;
    } else if (e.product_owner_id || e.product_owner_id === null) {
      resultClone.product_owner_id = e.product_owner_id;
    } else if (e.state) {
      resultClone.state = e.state;
    } else if (e.target && e.target.name && e.target.name === 'cadence_id') {
      resultClone[e.target.name] = e.target.value
        ? Number(e.target.value)
        : e.target.value;
    } else if (e.target && e.target.name && e.target.name === 'team_id') {
      resultClone[e.target.name] = e.target.value;
      resultClone.cadence_id = '';
    } else if (e.target && e.target.name) {
      resultClone[e.target.name] = e.target.value;
    } else {
      hasChanged = false;
    }
    this.setState({ result: resultClone, hasChanged });
  };

  handleTypeAttributes = (typeAttributes) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    if (result.result_type) {
      resultClone.result_type.type_attributes = typeAttributes;
      this.setState({ result: resultClone });
    }
  };

  handleFieldChange = (e) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    const field = e;
    const fieldName = Object.keys(field)[0];
    if (fieldName === 'short_name') {
      if (!field.short_name) {
        resultClone.short_name = field.short_name;
        return this.setState({ isShortNameValid: false, result: resultClone });
      }
      if (field.short_name === resultClone.short_name) return null;
    }
    this.props.updateWorkPackage(result.id, field);
    return null;
  };

  handleKeyPress = ({ key, target }) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    if (key === 'Enter' && !result.id) {
      resultClone[target.name] = target.value;
      this.setState({ result: resultClone }, () => this.createNewWorkPackage());
    }
  };

  addCriteria = (criterion) => {
    const { criteria } = this.state;
    let newCriteria = [...criteria];
    if (criterion.tempId) {
      newCriteria = newCriteria.map((item) => {
        if (item.tempId === criterion.tempId) {
          return {
            ...item,
            short_name: criterion.short_name
          };
        }
        return item;
      });
    } else newCriteria.push({ ...criterion, tempId: criteria.length + 1 });
    this.setState({ criteria: newCriteria });
  };

  removeCriteria = (criterionIndex) => {
    const { criteria } = this.state;
    const newCriteria = criteria.filter((c, index) => index !== criterionIndex);
    this.setState({ criteria: newCriteria });
  };

  addAttachment = (attachment) => {
    const { attachmentList } = this.state;
    const newAttachmentList = [...attachmentList];
    this.setState({ attachmentList: newAttachmentList.concat(attachment) });
  };

  removeAttachment = (attachmentIndex) => {
    const { attachmentList } = this.state;
    const newAttachmentList = attachmentList.filter(
      (att, index) => index !== attachmentIndex
    );
    this.setState({ attachmentList: newAttachmentList });
  };

  updateTemporaryTask = (temporaryId, attrs) => {
    const { tasks } = this.state;
    const tasksClone = cloneDeep(tasks);
    const newTasks = tasksClone.map((t) =>
      t.temporaryId === temporaryId ? { ...t, ...attrs } : t
    );
    this.setState({ tasks: newTasks });
  };

  updateTemporaryTaskOwner = (tempTaskId, ownerId) => {
    const { tasks } = this.state;
    const newTasks = tasks.map((task) => {
      if (task.temporaryId === tempTaskId) {
        return {
          ...task,
          task_owner_ids: ownerId ? [ownerId] : []
        };
      }
      return task;
    });
    this.setState({ tasks: newTasks });
  };

  createTemporaryTask = (tempTask, attachments = []) => {
    const task = tempTask;
    const { tasks, taskAttachments } = this.state;
    const taskIndex = tasks.findIndex(
      (t) => t.temporaryId === task.temporaryId
    );
    const taskAttachmentsClone = cloneDeep(taskAttachments);
    if (taskIndex > -1) {
      taskAttachmentsClone[taskIndex] = attachments;
      const newTasks = tasks.map((t) => {
        if (t.temporaryId === task.temporaryId) {
          return task;
        }
        return t;
      });
      this.setState({ taskAttachments: taskAttachmentsClone, tasks: newTasks });
    } else {
      task.temporaryId = tasks.length + 1;
      task.owner_initials = [];

      const newTasks = [...tasks];
      newTasks.push(task);

      const index = tasks.length;

      taskAttachmentsClone[index] = attachments;
      this.setState({
        tasks: newTasks,
        taskAttachments: taskAttachmentsClone,
        isTaskDetailOpen: false
      });
    }
  };

  removeTemporaryTask = (taskIndex) => {
    const { tasks, taskAttachments } = this.state;
    const newTasks = tasks.filter((t, index) => index !== taskIndex);
    const taskAttachmentsClone = cloneDeep(taskAttachments);

    if (taskAttachments[taskIndex]) {
      delete taskAttachmentsClone[taskIndex];
    }
    this.setState({ tasks: newTasks, taskAttachments: taskAttachmentsClone });
  };

  createNewWorkPackage = () => {
    sessionStorage.removeItem('result');
    sessionStorage.removeItem('criteria');
    sessionStorage.removeItem('attachmentList');
    sessionStorage.removeItem('tasks');
    const {
      result,
      criteria,
      attachmentList,
      tasks,
      taskAttachments
    } = this.state;
    const { organizationLevelId } = this.props;
    if (!result.short_name) {
      this.setState({ isShortNameValid: false });
      return;
    }
    const cadenceId = result.cadence_id;
    const resultType = {
      ...result.result_type,
      organization_level_id: organizationLevelId
    };
    const { state } = result;

    this.props.createWorkPackage({
      tasks,
      criteria,
      state,
      taskAttachments,
      data: { work_package: { ...result } },
      cadence: cadenceId,
      type: resultType,
      attachments: attachmentList,
      modalId: 'newResult'
    });
  };

  closeForm = () => {
    const { hasChanged, result, criteria, attachmentList, tasks } = this.state;

    if (hasChanged) {
      sessionStorage.setItem('result', JSON.stringify(result));
      sessionStorage.setItem('criteria', JSON.stringify(criteria));
      sessionStorage.setItem('attachmentList', JSON.stringify(attachmentList));
      sessionStorage.setItem('tasks', JSON.stringify(tasks));
      const message = 'Do you want to save the changes you made in this form?';
      const actions = {
        save: {
          name: 'Save',
          function: this.createNewWorkPackage
        },
        dontSave: {
          name: "Don't save",
          function: this.deleteStorageAndClose
        }
      };
      this.props.openModal(
        modalTypes.message,
        {
          message,
          actions,
          closeButtonTitle: 'Cancel'
        },
        'confirm'
      );
    } else {
      this.props.onClose();
    }
  };

  deleteStorageAndClose = () => {
    sessionStorage.removeItem('result');
    sessionStorage.removeItem('criteria');
    sessionStorage.removeItem('attachmentList');
    sessionStorage.removeItem('tasks');
    this.props.onClose();
  };

  changeTab = (tabName) => {
    this.setState({ activeTab: tabName });
  };

  render() {
    const {
      result,
      isTaskDetailOpen,
      activeTab,
      isShortNameValid
    } = this.state;

    const {
      teams,
      selectedSpace,
      products,
      parents,
      taskList,
      isSending,
      isOpen,
      organizationLevels,
      organizationLevelId
    } = this.props;

    const { product, team } = getModalProductTeam(
      result,
      selectedSpace,
      products,
      teams
    );

    const parent = parents.find((p) => p.id === result.parent_id) || {};
    if (!isOpen) {
      return null;
    }
    const targetId = result.target_id ? Number(result.target_id) : -1;

    const { workPackagedTasks, taskTypes } = taskList;
    const allowEdit = allows('update', result);
    let tasks = workPackagedTasks[result.id] || [];
    const onceCompleted = hasCompletedDevTask(tasks, taskTypes);

    if (!result.id) {
      tasks = cloneDeep(this.state.tasks);
    }
    const isLoading = isSending;

    const tabs = result.id
      ? ['details', 'tasks', 'metrics']
      : ['details', 'tasks'];

    return (
      <div
        id={`workpackage-detail-${result.id}`}
        className="detail-modal workpackage-detail"
        tabIndex="-1"
        role="dialog"
      >
        <div className="detail-dialog" role="document">
          {isLoading ? <div id="loader" /> : null}
          <div className={`detail-content ${isLoading ? 'blur-loading' : ''}`}>
            <div className="modal-header detail-header">
              <DetailsHeader
                type={this.props.type || result.result_type.kind}
                cadence={this.props.cadenceList?.cadence?.id}
                workPackage={result}
                organizationLevels={organizationLevels}
                organizationLevelId={organizationLevelId}
                options={this.props.options}
                onClose={this.closeForm}
                handleUpdate={this.handleFieldChange}
                handleRemove={this.props.removeWorkPackageFromCadence}
                handleDelete={this.props.deleteWorkPackage}
                handleRestore={this.props.restoreWorkPackage}
                handleTypeAttributes={this.handleTypeAttributes}
                selectedSpace={selectedSpace}
                changeOrgLevel={this.changeOrgLevel}
              />
            </div>
            <div
              className={`modal-body ${activeTab} ${result.id ? '' : 'is-new'}`}
            >
              <div className="short-name-box">
                <ShortNameField
                  autoFocus
                  shortName={result.short_name}
                  onNameChange={
                    result.id
                      ? this.handleFieldChange
                      : this.handleNewFieldChange
                  }
                  allowEdit={allowEdit}
                  onKeyUp={this.handleKeyPress}
                  isValid={isShortNameValid}
                />
                {result.id && <ShareButton />}
              </div>
              <ModalNavigation
                tabs={tabs}
                activeTab={activeTab}
                changeTab={this.changeTab}
              >
                <ResultDetailsTab
                  workPackage={result}
                  products={this.props.products}
                  product={product}
                  teams={this.props.teams}
                  team={team}
                  user={this.props.user}
                  parents={this.props.parents}
                  parent={parent}
                  handleUpdate={
                    result.id
                      ? this.handleFieldChange
                      : this.handleNewFieldChange
                  }
                  onSelectChange={this.handleSelectChange}
                  addCriteria={this.addCriteria}
                  removeCriteria={this.removeCriteria}
                  criteria={this.state.criteria}
                  attachmentList={this.state.attachmentList}
                  addAttachment={this.addAttachment}
                  removeAttachment={this.removeAttachment}
                  isTaskDetailOpen={isTaskDetailOpen}
                />
                <ResultTasksTab
                  tasks={tasks}
                  workPackage={result}
                  createTemporaryTask={this.createTemporaryTask}
                  removeTemporaryTask={this.removeTemporaryTask}
                  setTaskDetailOpen={this.setTaskDetailOpen}
                  taskAttachments={this.state.taskAttachments}
                  updateTemporaryTaskOwner={this.updateTemporaryTaskOwner}
                  updateTemporaryTask={this.updateTemporaryTask}
                />
                {result.id && (
                  <Metrics
                    activeTab={activeTab}
                    workPackage={result}
                    tasks={tasks}
                    cadences={this.props.cadenceList.cadences}
                  />
                )}
              </ModalNavigation>
            </div>
            <div className="modal-footer">
              <div className="row footer-selects">
                {!result.id && (
                  <div className="col-md-3 save-cancel-container">
                    <div className="save-cancel-buttons">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-md button-lavender"
                        onClick={this.createNewWorkPackage}
                        disabled={isLoading}
                      >
                        <strong>save</strong>
                      </button>
                      <button
                        type="button"
                        className="btn btn-md btn-default button-normal"
                        onClick={this.closeForm}
                        disabled={isLoading}
                      >
                        <strong>Cancel</strong>
                      </button>
                    </div>
                  </div>
                )}
                {!team ||
                  !team.id ||
                  (team && !team.kanban && (
                    <div className="col-md-3">
                      {this.state.activeTab === 'details' && (
                        <ResultCadenceSelect
                          workPackage={result}
                          cadenceList={this.props.cadenceList}
                          teamSelected={team}
                          allowEdit={allowEdit}
                          onSelect={this.handleSelectChange}
                          addWorkPackageToCadence={
                            this.props.addWorkPackageToCadence
                          }
                          removeWorkPackageFromCadence={
                            this.props.removeWorkPackageFromCadence
                          }
                          cadenceSelected={this.props.cadenceSelected}
                        />
                      )}
                    </div>
                  ))}
                <div className="col-md-3">
                  {this.state.activeTab === 'details' && (
                    <TargetSelect
                      label="Target"
                      selected={targetId || -1}
                      disabled={!allowEdit}
                      onSelect={this.handleSelectChange}
                      workPackage={result}
                      fullWidth
                    />
                  )}
                </div>
              </div>
              <ResultDetailsStatus
                result={result}
                status={result.state || WorkPackageStatuses.NOT_READY}
                allowEdit={allowEdit}
                handleFieldChange={this.handleNewFieldChange}
                blockStatus={onceCompleted}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ResultDetail.defaultProps = {
  result: {}
};

ResultDetail.propTypes = {
  result: PropTypes.object,
  cadenceList: PropTypes.object,
  taskList: PropTypes.object,
  options: PropTypes.object,
  isOpen: PropTypes.bool,
  type: PropTypes.string,
  isFetching: PropTypes.bool,
  products: PropTypes.array,
  currentProducts: PropTypes.array,
  currentTeams: PropTypes.array,
  teams: PropTypes.array,
  onClose: PropTypes.func,
  deleteWorkPackage: PropTypes.func,
  updateWorkPackage: PropTypes.func,
  addWorkPackageToTarget: PropTypes.func,
  addWorkPackageToCadence: PropTypes.func,
  removeWorkPackageFromCadence: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
  const { features, capabilities, epics } = state.workPackageReducer;
  const parents = features.concat(capabilities).concat(epics);
  const { organizations = [] } = state.organizationReducer;

  // the following would break with the addition of spaces in another org
  // todo: grab selected space's org Id, and find org that way
  const organizationLevels =
    organizations.length > 0 ? organizations[0].organization_levels : [];
  const result = ownProps.result
    ? ownProps.result
    : state.workPackageReducer.all.find((w) => ownProps.id === w.id);

  const planwallState = state.savedViewReducer.planwallStates.find(
    (pws) => pws.level === 1
  );

  return {
    parents,
    organizationLevels,
    result,
    planwallState,
    kanbanWallState: state.savedViewReducer.kanbanWallState,
    user: state.userReducer.user,
    users: state.userReducer.users,
    cadenceList: state.cadenceReducer,
    taskList: state.taskReducer,
    products: state.productReducer.products,
    options: state.defaultOptionsReducer.defaultOptions,
    isSending: state.workPackageReducer.isSending,
    isUploading: state.attachmentReducer.isUploading,
    isFetching: state.attachmentReducer.isFetching,
    currentProducts: state.defaultOptionsReducer.currentProducts,
    currentTeams: state.defaultOptionsReducer.currentTeams,
    teams: state.teamReducer.teams,
    cadenceSelected: state.cadenceReducer.cadence,
    targets: state.targetReducer.targets,
    selectedSpace: state.workPackageReducer.selectedSpace
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateWorkPackage,
      addWorkPackageToCadence,
      removeWorkPackageFromCadence,
      deleteWorkPackage,
      createWorkPackage,
      openModal,
      updateModal,
      applicationError,
      restoreWorkPackage
    },
    dispatch
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ResultDetail)
);
